import React, { useEffect, useState } from 'react';
import { Moment } from 'moment';
import { PersonalDetailsInputs, PolicyholderPrefillValues } from './personal-details';
import { ActiveElement } from 'rootstrap/components/forms/new-fields/utils';
import { usePromise } from 'shared/hooks/promise';
import { useForm } from 'react-hook-form';
import { CardGroup } from 'rootstrap/components/card';
import { debounce } from 'utils';
import { Col, Row } from 'reactstrap';
import { InputField, InputFieldDisplayProperties } from 'rootstrap/components/forms/new-fields/input-field';
import { ValidationTypes } from 'rootstrap/components-old/root-schema-form/utils/validation';
import { DatePickerField } from 'rootstrap/components/forms/new-fields/date-picker-field';
import { PrimitiveListField } from 'rootstrap/components/forms/new-fields/primitive-list-field';
import { useSiteConfigContext } from 'style-context';
import { getWording, isReadOnly } from 'site-config';
import { getInputFieldConfig } from '../utils/helpers';
import { PolicyholderLookUpFormData } from './unauthenticated-policyholder-lookup';
import { SteppedComponentsBehavior } from 'rootstrap/components-old/root-schema-form/utils/stepped-components-behavior';

export interface PersonalDetailsCompanyDetailsData {
  companyName?: string;
  registrationNumber?: string;
  companyWebsiteUrl?: string;
  contactPosition?: string;
  dateOfEstablishment?: Moment;
  subsidiaryCompanies?: string[];
}

interface Props {
  setIsValid: (params: boolean) => void;
  setSectionData: (params: PersonalDetailsCompanyDetailsData) => void;
  personalDetailCompanyFormSectionData: PersonalDetailsCompanyDetailsData | undefined;
  prefillValues: PolicyholderPrefillValues;
  setActiveElement: (params: ActiveElement) => void;
  activeElement: ActiveElement;
  isCompleted: boolean;
  isPolicyholderTypeFieldDisplayed: boolean;
  policyholderLookupFormData: PolicyholderLookUpFormData | undefined;
  disableSteppedComponent?: boolean;
  disableIdEdit?: boolean;
  isManagementContext: boolean | undefined;
}

export const CompanyDetailsSection = (props: Props) => {
  const {
    activeElement,
    isCompleted,
    isPolicyholderTypeFieldDisplayed,
    personalDetailCompanyFormSectionData,
    prefillValues,
    setActiveElement,
    setIsValid,
    setSectionData,
    policyholderLookupFormData,
  } = props;
  const { siteConfig } = useSiteConfigContext();

  const [isTouched] = useState<boolean>(isCompleted);

  const form = useForm<PersonalDetailsCompanyDetailsData>({
    mode: 'onChange',
    defaultValues: personalDetailCompanyFormSectionData,
  });

  usePromise(async () => {
    await form.trigger();
  }, []);

  usePromise(async () => {
    setIsValid(form.formState.isValid);
  }, [form.formState.isValid]);

  const execute = () => {
    const formValues = form.getValues() as PersonalDetailsCompanyDetailsData;
    setSectionData({ ...formValues, subsidiaryCompanies: formValues.subsidiaryCompanies || [] });
  };

  useEffect(execute, []);

  const disableSteppedComponents =
    !!siteConfig?.styles.disableSteppedComponents || Boolean(props.disableSteppedComponent);

  const steppedComponentsBehavior: SteppedComponentsBehavior = {
    disableNextButton: disableSteppedComponents,
    hideDivider: disableSteppedComponents,
    disableScrollToElement: disableSteppedComponents,
    disableActiveElement: disableSteppedComponents,
    isTouched: !disableSteppedComponents ? isCompleted : true,
  };
  const isPersonalDetailsReadOnly = isReadOnly(props.isManagementContext, siteConfig, 'personalDetails');

  return (
    <CardGroup>
      <form
        onBlur={() => debounce('', () => execute())}
        onChange={() => execute()}
        onSubmit={form.handleSubmit((data: PersonalDetailsCompanyDetailsData) => setSectionData(data))}
      >
        <Row>
          <Col sm={12}>
            <InputField
              defaultValue={
                policyholderLookupFormData?.registrationNumber ||
                personalDetailCompanyFormSectionData?.registrationNumber
              }
              isDisabled={
                Boolean(policyholderLookupFormData?.registrationNumber) ||
                props.disableIdEdit ||
                !!isPersonalDetailsReadOnly
              }
              placeholder={undefined}
              name={PersonalDetailsInputs.RegistrationNumber}
              label={getWording({
                wording: getInputFieldConfig('registrationNumber', props.isManagementContext, siteConfig)?.label,
              })}
              prefillAction={
                getInputFieldConfig('registrationNumber', props.isManagementContext, siteConfig)?.prefillAction
              }
              form={form}
              validators={[
                {
                  validation: {
                    type: ValidationTypes.REQUIRED,
                  },
                },
              ]}
              prefillValue={prefillValues.registrationNumber}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: PersonalDetailsInputs.CompanyName,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              defaultValue={personalDetailCompanyFormSectionData?.companyName}
              placeholder={undefined}
              name={PersonalDetailsInputs.CompanyName}
              label={getWording({
                wording: getInputFieldConfig('companyName', props.isManagementContext, siteConfig)?.label,
              })}
              prefillAction={getInputFieldConfig('companyName', props.isManagementContext, siteConfig)?.prefillAction}
              form={form}
              validators={[
                {
                  validation: {
                    type: ValidationTypes.REQUIRED,
                  },
                },
              ]}
              prefillValue={prefillValues.companyName}
              isDisabled={isPersonalDetailsReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: PersonalDetailsInputs.DateOfEstablishment,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <DatePickerField
              defaultValue={personalDetailCompanyFormSectionData?.dateOfEstablishment}
              placeholder={undefined}
              name={PersonalDetailsInputs.DateOfEstablishment}
              label={getWording({
                wording: getInputFieldConfig('dateOfEstablishment', props.isManagementContext, siteConfig)?.label,
              })}
              prefillAction={
                getInputFieldConfig('dateOfEstablishment', props.isManagementContext, siteConfig)?.prefillAction
              }
              form={form}
              validators={[
                {
                  validation: {
                    type: ValidationTypes.REQUIRED,
                  },
                },
              ]}
              prefillValue={prefillValues.companyWebsiteUrl}
              isDisabled={isPersonalDetailsReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: PersonalDetailsInputs.SubsidiaryCompanies,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <PrimitiveListField
              defaultValues={personalDetailCompanyFormSectionData?.subsidiaryCompanies || []}
              placeholder={undefined}
              name={PersonalDetailsInputs.SubsidiaryCompanies}
              label={getWording({
                wording: getInputFieldConfig('subsidiaryCompanies', props.isManagementContext, siteConfig)?.label,
              })}
              prefillAction={
                getInputFieldConfig('subsidiaryCompanies', props.isManagementContext, siteConfig)?.prefillAction
              }
              form={form}
              validators={[]}
              prefillValue={prefillValues.subsidiaryCompanies}
              isDisabled={isPersonalDetailsReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: PersonalDetailsInputs.CompanyWebsiteUrl,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              defaultValue={personalDetailCompanyFormSectionData?.companyWebsiteUrl}
              placeholder={undefined}
              name={PersonalDetailsInputs.CompanyWebsiteUrl}
              label={getWording({
                wording: getInputFieldConfig('companyWebsiteUrl', props.isManagementContext, siteConfig)?.label,
              })}
              prefillAction={
                getInputFieldConfig('companyWebsiteUrl', props.isManagementContext, siteConfig)?.prefillAction
              }
              form={form}
              validators={[
                {
                  validation: {
                    type: ValidationTypes.REQUIRED,
                  },
                },
                {
                  validation: {
                    type: ValidationTypes.URL,
                  },
                },
              ]}
              prefillValue={prefillValues.companyWebsiteUrl}
              isDisabled={isPersonalDetailsReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: PersonalDetailsInputs.ContactPosition,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              defaultValue={personalDetailCompanyFormSectionData?.contactPosition}
              placeholder={undefined}
              name={PersonalDetailsInputs.ContactPosition}
              label={getWording({
                wording: getInputFieldConfig('contactPosition', props.isManagementContext, siteConfig)?.label,
              })}
              prefillAction={
                getInputFieldConfig('contactPosition', props.isManagementContext, siteConfig)?.prefillAction
              }
              form={form}
              validators={[
                {
                  validation: {
                    type: ValidationTypes.REQUIRED,
                  },
                },
              ]}
              prefillValue={prefillValues.contactPosition}
              isDisabled={isPersonalDetailsReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: PersonalDetailsInputs.FirstName,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
      </form>
    </CardGroup>
  );
};
